package dev.lingvo.site.pages.app.projects

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.COMMON_LOCALES
import dev.lingvo.shared.CreateProjectRequest
import dev.lingvo.shared.ProjectAccentKey
import dev.lingvo.shared.ProjectIconKey
import dev.lingvo.shared.ProjectKind
import dev.lingvo.shared.localeFlagPrefix
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LFileIcon
import dev.lingvo.site.ds.LLanguagesIcon
import dev.lingvo.site.ds.LPlusIcon
import dev.lingvo.site.ds.LProjectMark
import dev.lingvo.site.ds.LSelect
import dev.lingvo.site.ds.LSelectOption
import dev.lingvo.site.ds.LTextArea
import dev.lingvo.site.ds.LTextField
import dev.lingvo.site.ds.LToastVariant
import dev.lingvo.site.ds.LocalToasts
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TOAST_UNTIL_DISMISSED
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.pages.app.projects.slug.settings.ProjectIdentityPicker
import dev.lingvo.site.pages.app.projects.slug.settings.displayLabel
import dev.lingvo.site.state.ProjectDirectory
import dev.lingvo.site.state.ProjectRouteContext
import dev.lingvo.site.state.WorkspaceRequestContext
import dev.lingvo.site.state.WorkspaceRequestKind
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

@Page("/app/projects/new")
@Layout(".components.layouts.AppLayout")
@Composable
fun NewProjectPage() {
    val ctx = rememberPageContext()
    val scope = rememberCoroutineScope()
    val toasts = LocalToasts.current
    val workspaceContext = WorkspaceRequestContext.current()
    val createDecision = workspaceContext?.decision(WorkspaceRequestKind.CREATE_PROJECT)
    val canCreate = workspaceContext?.canManageProjects == true && createDecision?.allowed == true
    var name by remember { mutableStateOf("") }
    var description by remember { mutableStateOf("") }
    var kind by remember { mutableStateOf(ProjectKind.OTHER) }
    var iconKey by remember { mutableStateOf(ProjectIconKey.MONOGRAM) }
    var accentKey by remember { mutableStateOf(ProjectAccentKey.INK) }
    var sourceLocale by remember { mutableStateOf("en") }
    var targetLocales by remember { mutableStateOf(emptySet<String>()) }
    var targetSearch by remember { mutableStateOf("") }
    var submitted by remember { mutableStateOf(false) }
    var creating by remember { mutableStateOf(false) }
    var projectLimitReached by remember(workspaceContext?.key) { mutableStateOf(false) }
    val slug = projectSlug(name)
    // `tr`, not `t`: this is a value assigned outside composition's argument position and was the
    // one message on the form that never reached the catalogue.
    val nameError = if (submitted && name.isBlank()) {
        tr("app.projects.new.enter_a_project_name", "Enter a project name")
    } else {
        null
    }
    val targetsError = submitted && targetLocales.isEmpty()
    // The name the preview and the slug line stand in for before anything is typed.
    val provisionalName = t("app.projects.new.untitled_project", "Untitled project")

    val create: () -> Unit = {
        submitted = true
        if (name.isNotBlank() && targetLocales.isNotEmpty() && workspaceContext != null) {
            scope.launch {
                creating = true
                projectLimitReached = false
                try {
                    val created = ApiClient.createProject(
                        CreateProjectRequest(
                            name = name.trim(),
                            orgSlug = workspaceContext.workspace.slug,
                            // Deliberately not sent. Project slugs are unique across the whole
                            // instance, and a slug this page names is one the server must take
                            // verbatim or refuse — which it did, as a six-second toast about a
                            // stranger's project, on a page whose own project list is empty. Left
                            // to derive it, the server suffixes until one is free. Navigation below
                            // already uses the slug it answers with.
                            slug = null,
                            sourceLocale = sourceLocale,
                            targetLocales = COMMON_LOCALES
                                .map { it.code }
                                .filter { it in targetLocales && it != sourceLocale },
                            description = description.trim().ifBlank { null },
                            kind = kind,
                            iconKey = iconKey,
                            accentKey = accentKey,
                        ),
                    )
                    // **The project exists the moment the call above returns.** Everything below is
                    // cache warming for the screen we are about to open, and a seed that declines —
                    // the session changed mid-request, the response named another workspace — is not
                    // a creation that failed. Asserting on it reported a *successful* create as
                    // "Couldn't create the project", left the form standing, and the obvious retry
                    // made a second project.
                    ProjectRouteContext.seed(workspaceContext, created)
                    ProjectDirectory.seed(workspaceContext, created)
                    ProjectDirectory.invalidate(workspaceContext)
                    toasts.show(tr("app.projects.new.project_created", "Project created"), LToastVariant.SUCCESS)
                    ctx.router.tryRoutingTo(
                        workspaceContext.createdProjectRoute(created)
                            ?: "/app/projects/${created.slug}",
                    )
                } catch (cancelled: CancellationException) {
                    throw cancelled
                } catch (failure: Throwable) {
                    if (isProjectLimitFailure(failure)) {
                        projectLimitReached = true
                    } else {
                        toasts.show(
                            failure.message?.takeIf(String::isNotBlank)
                                ?: tr(
                                    "app.projects.new.couldnt_create_the_project_check_your",
                                    "Couldn't create the project. Check your connection and retry.",
                                ),
                            LToastVariant.DANGER,
                            durationMillis = TOAST_UNTIL_DISMISSED,
                        )
                    }
                } finally {
                    creating = false
                }
            }
        }
    }

    Column(Modifier.fillMaxWidth().gap(Space.XXL)) {
        // The form's one primary action, in the slot the product keeps for a page's one primary
        // action. It stays reachable while the language picker is scrolled, which is where a long
        // form loses people.
        PageHeader(
            title = t("app.projects.new.new_project", "New project"),
            subtitle = t("app.projects.new.set_the_language_direction_now_files", "Set the language direction now; files and keys can follow."),
        ) {
            if (canCreate) {
                LButton(
                    label = t("app.projects.new.cancel", "Cancel"),
                    onClick = { ctx.router.tryRoutingTo("/app") },
                    variant = LButtonVariant.GHOST,
                    enabled = !creating,
                )
                LButton(
                    label = t("app.projects.new.create_project", "Create project"),
                    onClick = create,
                    // Not `!creating`. `loading` already stops the click and announces
                    // `aria-busy`; adding `disabled` on top removes the button from the focus
                    // order mid-action, so a keyboard reader who pressed Create is dropped to
                    // the document while it works and has nothing to return to.
                    loading = creating,
                    leadingIcon = { LPlusIcon() },
                )
            }
        }
        if (!canCreate) {
            // Three refusals that send the reader to three different people, and they used to be
            // one sentence with the server-capability answer winning: a translator in a
            // multi-workspace account read "This server cannot safely separate this action between
            // multiple workspaces yet" rather than being told about their role. `/app` and
            // `/app/start` test the role first for exactly that reason, and their title names the
            // role rather than blaming a workspace that is not the problem.
            when {
                workspaceContext == null -> LAlert(
                    title = t("app.projects.new.no_workspace_selected", "No workspace selected"),
                    description = t("app.projects.new.a_workspace_holds_the_project_you", "A workspace holds the project you are about to make. Create one, or ask " +
                        "whoever invited you to send the invitation again."),
                    modifier = Modifier.fillMaxWidth(),
                    variant = LAlertVariant.INFO,
                )
                workspaceContext?.canManageProjects != true -> LAlert(
                    title = t("app.projects.new.only_an_owner_or_an_admin", "Only an owner or an admin can add a project"),
                    description = t("app.projects.new.ask_one_of_them_to_create", "Ask one of them to create it, or open a project that is already shared " +
                        "with you."),
                    modifier = Modifier.fillMaxWidth(),
                    variant = LAlertVariant.INFO,
                )
                else -> LAlert(
                    title = t("app.projects.new.this_workspace_cannot_be_addressed_yet", "This workspace cannot be addressed yet"),
                    description = createDecision?.refusal
                        ?: t("app.projects.new.only_workspace_owners_and_admins_can", "Only workspace owners and admins can create a project here."),
                    modifier = Modifier.fillMaxWidth(),
                    variant = LAlertVariant.INFO,
                )
            }
            return@Column
        }
        if (projectLimitReached) ProjectLimitNotice(workspaceContext?.role)
        LCard(Modifier.fillMaxWidth().maxWidth(56.cssRem)) {
            Column(Modifier.fillMaxWidth().gap(Space.XXL)) {
                FormSection(
                    title = t("app.projects.new.project_details", "Project details"),
                    description = t("app.projects.new.a_clear_name_and_a_little", "A clear name and a little context help translators make better choices."),
                    icon = { LFileIcon() },
                ) {
                    LTextField(
                        value = name,
                        onValueChange = { name = it },
                        label = t("app.projects.new.project_name", "Project name"),
                        placeholder = t("app.projects.new.atlas_mobile", "Atlas mobile"),
                        error = nameError,
                        enabled = !creating,
                    )
                    LTextArea(
                        value = description,
                        onValueChange = { description = it },
                        label = t("app.projects.new.description_optional", "Description (optional)"),
                        placeholder = t("app.projects.new.what_the_product_does_and_where", "What the product does and where this copy appears"),
                        enabled = !creating,
                    )
                    LSelect(
                        value = kind.name,
                        options = ProjectKind.entries.map { value ->
                            LSelectOption(value.name, value.displayLabel())
                        },
                        onValueChange = { kind = ProjectKind.valueOf(it) },
                        label = t("app.projects.new.project_kind", "Project kind"),
                        enabled = !creating,
                    )
                    Row(
                        Modifier.fillMaxWidth().gap(Space.MD),
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        LProjectMark(name.ifBlank { provisionalName }, iconKey, accentKey)
                        Column(Modifier.gap(Space.XS)) {
                            Span(Modifier.fontWeight(700).toAttrs()) { Text(name.ifBlank { provisionalName }) }
                            Span(Modifier.color(ColorMode.current.toSitePalette().textSecondary).fontSize(TypeScale.Small).toAttrs()) {
                                Text(tf("app.projects.new.slug_and_kind", "{slug} · {kind}", "slug" to slug, "kind" to kind.displayLabel()))
                            }
                        }
                    }
                    ProjectIdentityPicker(
                        name = name,
                        iconKey = iconKey,
                        accentKey = accentKey,
                        onIconSelected = { iconKey = it },
                        onAccentSelected = { accentKey = it },
                    )
                    SlugPreview(slug)
                }

                FormSection(
                    title = t("app.projects.new.languages", "Languages"),
                    description = t("app.projects.new.the_source_language_is_the_approved", "The source language is the approved reference for every target."),
                    icon = { LLanguagesIcon() },
                ) {
                    LSelect(
                        value = sourceLocale,
                        options = COMMON_LOCALES.map { locale ->
                            LSelectOption(
                                value = locale.code,
                                label = "${localeFlagPrefix(locale.code)}${locale.englishName} — ${locale.code}",  // i18n-ignore — a flag glyph immediately followed by a language name — no separator, no words, nothing a translator could change
                            )
                        },
                        onValueChange = { selected ->
                            sourceLocale = selected
                            targetLocales = targetLocales - selected
                        },
                        label = t("app.projects.new.source_locale", "Source language"),
                        enabled = !creating,
                    )
                    TargetLocalePicker(
                        sourceLocale = sourceLocale,
                        selected = targetLocales,
                        search = targetSearch,
                        enabled = !creating,
                        showError = targetsError,
                        onSearchChange = { targetSearch = it },
                        onSelectedChange = { targetLocales = it - sourceLocale },
                    )
                }
            }
        }
    }
}
